import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import client from "@/lib/api";
import { FeedPathTag } from "@/components/FeedPathTag";
import { useI18n } from "@/context/I18nContext";
import { Pencil, Save, UserRound } from "lucide-react";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const FIELDS = [
  ["name_internal", "dealership.internalContact.name", "Dealer.WebGenContact.name"],
  ["email_internal", "dealership.internalContact.email", "Dealer.WebGenContact.email"],
  ["phone_internal", "dealership.internalContact.phone", "Dealer.WebGenContact.phone"],
];

/** Internal contact for SAD — populated from the import feed's WebGenContact
 * block while the dealer isn't DDM-migrated, editable by anyone holding
 * dealer.internal_contact.edit (every MHQ role + Dealer Main User; the card
 * is only rendered for them). Never part of any export feed — see
 * Dealer.name_internal/email_internal/phone_internal in models.py. */
export function InternalContactCard({ dealer, dealerId, onSaved }) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const start = () => {
    setDraft(Object.fromEntries(FIELDS.map(([key]) => [key, dealer[key] || ""])));
    setEditing(true);
  };

  const email = (draft?.email_internal || "").trim();
  const emailInvalid = editing && email !== "" && !EMAIL_RE.test(email);

  const save = async () => {
    if (emailInvalid) return;
    setSaving(true);
    try {
      const { data } = await client.patch(`/dealers/${dealerId}/internal-contact`, draft);
      onSaved(data);
      setEditing(false);
      toast.success(t("dealership.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-6 p-6" data-testid="internal-contact-card">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <UserRound className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-lg font-semibold">{t("dealership.internalContact.title")}</h2>
        </div>
        {!editing ? (
          <Button data-testid="edit-internal-contact-btn" variant="ghost" size="sm" className="gap-1.5" onClick={start}>
            <Pencil className="h-3.5 w-3.5" />
            {t("common.edit")}
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              {t("common.cancel")}
            </Button>
            <Button data-testid="save-internal-contact-btn" size="sm" onClick={save} disabled={saving || emailInvalid}className="gap-1.5">
              <Save className="h-3.5 w-3.5" />
              {t("common.save")}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {FIELDS.map(([key, labelKey, feedPath]) => (
          <div key={key}>
            <Label className="data-label mb-1 flex items-center gap-1">
              {t(labelKey)}
              <FeedPathTag path={feedPath} />
            </Label>
            {editing ? (
              <>
                <Input
                  data-testid={`internal-contact-${key}`}
                  disabled={saving}
                  type={key === "email_internal" ? "email" : "text"}
                  value={draft?.[key] ?? ""}
                  aria-invalid={key === "email_internal" && emailInvalid}
                  className="text-sm"
                  onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                />
                {key === "email_internal" && emailInvalid && (
                  <p className="mt-1 text-xs text-destructive" data-testid="internal-contact-email-error">
                    {t("dealership.internalContact.emailInvalid")}
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm" data-testid={`internal-contact-${key}`}>
                {dealer[key] || "—"}
              </p>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
